import { clearTabSessionId, getTabSessionId } from "./tabSessionId";

const OWNERSHIP_CHANNEL_NAME = "tab_session_ownership";
const OWNERSHIP_CLAIM_TIMEOUT_MS = 300;

type ClaimMessage = { type: "CLAIM"; sessionId: string; from: string };
type OwnedMessage = { type: "OWNED"; sessionId: string; to: string };
type OwnershipMessage = ClaimMessage | OwnedMessage;

const createInstanceId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// Regenerated on every page load and never persisted, so a duplicated tab can
// never carry this value over from the tab it was cloned from.
const instanceId = createInstanceId();

let channel: BroadcastChannel | null = null;
let hasSettledOwnClaim = false;
let pendingCheck: Promise<boolean> | null = null;

function getOwnershipChannel(): BroadcastChannel | null {
  if (typeof globalThis.BroadcastChannel === "undefined") {
    return null;
  }

  if (channel) {
    return channel;
  }

  try {
    channel = new BroadcastChannel(OWNERSHIP_CHANNEL_NAME);
  } catch {
    return null;
  }

  channel.addEventListener("message", (event: MessageEvent<OwnershipMessage>) => {
    const message = event.data;

    if (message?.type !== "CLAIM" || message.from === instanceId) {
      return;
    }

    // Only a tab that already settled its own claim answers. Two tabs claiming
    // at the same time therefore never evict each other.
    if (!hasSettledOwnClaim || getTabSessionId() !== message.sessionId) {
      return;
    }

    const reply: OwnedMessage = { type: "OWNED", sessionId: message.sessionId, to: message.from };
    channel?.postMessage(reply);
  });

  return channel;
}

function claimSession(sessionId: string, ownershipChannel: BroadcastChannel): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const cleanup = () => {
      clearTimeout(timeout);
      ownershipChannel.removeEventListener("message", handleMessage);
    };

    function handleMessage(event: MessageEvent<OwnershipMessage>) {
      const message = event.data;

      if (
        message?.type === "OWNED" &&
        message.to === instanceId &&
        message.sessionId === sessionId
      ) {
        cleanup();
        resolve(true);
      }
    }

    const timeout = setTimeout(() => {
      cleanup();
      resolve(false);
    }, OWNERSHIP_CLAIM_TIMEOUT_MS);

    ownershipChannel.addEventListener("message", handleMessage);

    const claim: ClaimMessage = { type: "CLAIM", sessionId, from: instanceId };
    ownershipChannel.postMessage(claim);
  });
}

/**
 * Detects whether this tab was created by the browser's "Duplicate tab" action.
 *
 * Duplicating a tab copies sessionStorage and window.name, so the clone starts
 * with the original tab's session ID and reads the same `auth_token_{sessionId}`
 * cookie - which is where the selected culture lives. Storage alone cannot tell
 * a duplicate from a reload, so ownership is resolved by asking the other tabs:
 * a reload gets no answer (the previous page is already gone), while a duplicate
 * gets an answer from the still-open original.
 *
 * @returns true when another live tab already owns the current session ID
 */
export function isDuplicatedTabSession(): Promise<boolean> {
  if (pendingCheck) {
    return pendingCheck;
  }

  pendingCheck = (async () => {
    const sessionId = getTabSessionId();
    const ownershipChannel = getOwnershipChannel();

    if (!sessionId || !ownershipChannel) {
      hasSettledOwnClaim = true;
      return false;
    }

    const isDuplicate = await claimSession(sessionId, ownershipChannel);
    hasSettledOwnClaim = true;

    return isDuplicate;
  })();

  return pendingCheck;
}

/**
 * Drops the inherited session ID when this tab is a duplicate, so the caller
 * bootstraps a brand new session (and therefore the default culture).
 *
 * @returns true when an inherited session was released
 */
export async function releaseDuplicatedTabSession(): Promise<boolean> {
  const isDuplicate = await isDuplicatedTabSession();

  if (isDuplicate) {
    clearTabSessionId();
  }

  return isDuplicate;
}
